import { Controller, useForm } from 'react-hook-form';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEstate_GetEstatesByPortalIdQuery, useEstate_UpdateEstateMutation } from 'queries';

import FormSelectField from 'common/form-select-field';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
  },
  selectInput: {
    borderRadius: '8px',
    '& fieldset': {
      border: '1px solid #ECEEF1',
    },
  },
  formItems: {
    textAlign: 'left',
    marginTop: '40px',
  },
  assignButton: {
    borderRadius: '100px',
    maxHeight: '40px',
    marginTop: '40px',
    marginBottom: '20px',
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
  },
}));

type Props = {
  portalId: string;
  onClose: () => void;
  userId: string;
};

type AssignToEstateForm = {
  userId: string;
  portalId: string;
  estateId: string;
};

const AssignToEstateModal = ({ portalId, onClose, userId }: Props) => {
  const classes = useStyles();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<AssignToEstateForm>({
    defaultValues: {
      userId: userId,
      portalId: portalId,
    },
  });

  const {
    data: estatesData,
    refetch: refetchEstates,
    loading: loadingEstates,
  } = useEstate_GetEstatesByPortalIdQuery({
    variables: { input: { portalId } },
    onError: (e) => {
      showAlert({
        message: 'Error finding estates by portal ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updateEstate] = useEstate_UpdateEstateMutation({
    onCompleted: () => {
      showAlert({
        message: 'Estate updated successfully',
        severity: NotificationSeverity.SUCCESS,
      });
      refetchEstates();
      reset();
      onClose();
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const onSubmit = async (data: AssignToEstateForm) => {
    await updateEstate({
      variables: {
        input: {
          id: data.estateId,
          portalId: portalId,
          assignedUserId: userId,
        },
      },
    });
  };

  if (loadingEstates) {
    return <Spinner />;
  }

  return (
    <Modal
      setShowModal={() => onClose()}
      onExitClick={onClose}
      showModal={true}
      showActions={false}
      content={
        <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
          <Typography className={classes.title}>Assign estate</Typography>
          <div className={classes.formItems}>
            <Controller
              name="estateId"
              control={control}
              render={({ field: { ref: _, ...field } }) => (
                <FormSelectField
                  {...field}
                  errorMessage={errors.estateId?.message}
                  options={
                    estatesData?.data.map((estate) => ({
                      label: estate.estateName,
                      value: estate.id,
                    })) || []
                  }
                  label="Estate"
                  className={classes.selectInput}
                />
              )}
            />
          </div>
          <Button
            type="submit"
            variant="outlined"
            className={classes.assignButton}
            disabled={isSubmitting}
          >
            Assign
          </Button>
        </form>
      }
    />
  );
};

export default AssignToEstateModal;
